/*
 * Live free-shipping progress on the product page.
 *
 * The bar is rendered server-side from the cart, so it is already correct on
 * load and works with JavaScript off. This only keeps it in step afterwards:
 * the theme dispatches on:cart:add and on:line-item:change when the cart
 * changes without a page load, and both are answered by re-reading /cart.js
 * rather than trusting the event payload, which carries the added line rather
 * than the new total.
 *
 * Money is formatted from shop.money_format, the same template Liquid's money
 * filters use, rather than from Intl. Intl was producing "€ 535,01" against
 * Liquid's "€750" on the same card - right locale, wrong house style.
 */
if (!customElements.get('knuska-trust-shipping')) {
  class KnuskaTrustShipping extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold);
      this.fill = this.querySelector('[data-trust-fill]');
      this.status = this.querySelector('[data-trust-status]');
      this.bar = this.querySelector('[role="progressbar"]');
      if (!this.threshold || !this.fill) return;

      this.onCartChange = this.refresh.bind(this);
      document.addEventListener('on:cart:add', this.onCartChange);
      document.addEventListener('on:line-item:change', this.onCartChange);
      document.addEventListener('on:cart:change', this.onCartChange);
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:add', this.onCartChange);
      document.removeEventListener('on:line-item:change', this.onCartChange);
      document.removeEventListener('on:cart:change', this.onCartChange);
    }

    async refresh() {
      let cart;
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return;
        cart = await response.json();
      } catch (e) {
        return;
      }

      const total = Number(cart.total_price) || 0;
      const remaining = Math.max(this.threshold - total, 0);
      const percent = Math.min(Math.round((total / this.threshold) * 100), 100);

      this.fill.style.width = `${percent}%`;
      if (this.bar) this.bar.setAttribute('aria-valuenow', String(percent));
      this.classList.toggle('knuska-tc--reached', remaining === 0);

      if (!this.status) return;

      if (remaining === 0) {
        this.status.textContent = this.dataset.reachedText || '';
        return;
      }

      this.status.textContent = (this.dataset.remainingText || '').replace(
        '[amount]',
        this.formatMoney(remaining)
      );
    }

    /**
     * Renders cents through shop.money_format, then drops a trailing zero
     * decimal the way money_without_trailing_zeros does - so a figure written
     * here is identical to one written by Liquid.
     */
    formatMoney(cents) {
      const format = this.dataset.moneyFormat;
      if (!format) return String(cents / 100);

      const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!placeholder) return format;

      const group = (value, thousands, decimal, places) => {
        const fixed = (value / 100).toFixed(places);
        const [whole, fraction] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fraction ? `${grouped}${decimal}${fraction}` : grouped;
      };

      let amount;
      switch (placeholder[1]) {
        case 'amount_no_decimals':
          amount = group(cents, ',', '.', 0);
          break;
        case 'amount_with_comma_separator':
          amount = group(cents, '.', ',', 2);
          break;
        case 'amount_no_decimals_with_comma_separator':
          amount = group(cents, '.', ',', 0);
          break;
        case 'amount_with_apostrophe_separator':
          amount = group(cents, "'", '.', 2);
          break;
        case 'amount_with_space_separator':
        case 'amount_with_period_and_space_separator':
          amount = group(cents, ' ', ',', 2);
          break;
        default:
          amount = group(cents, ',', '.', 2);
      }

      return format
        .replace(placeholder[0], amount)
        .replace(/([.,])00(?=\D*$)/, '');
    }
  }

  customElements.define('knuska-trust-shipping', KnuskaTrustShipping);
}
